import * as React from 'react';
import { bindActionCreators, Dispatch, AnyAction } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { DropdownButtonType, LocallyFilteringList, SingleSelectDropdown } from '@tableau/widgets';
import { Messages } from '../../../../../gen-src/Localize';
import { AppState } from '../../../../app/state/AppState';
import { DropdownBlueButton, DropdownDefaultButton, FilteringListClass } from '../../../styles/FilterStyles';
import { FilterTypes } from '../../../models/FilterTypes';
import { updateFilter } from '../../../actions/UpdateFilterAction';
import { QuickFilter, QuickFilterDispatchProps, QuickFilterOwnProps, QuickFilterStateProps } from '../QuickFilter';
import { RouteComponentProps } from 'react-router-dom';
import { FilterHelpers, ListItemWithDisplayText } from '../../../../common/utils/FilterHelpers';
import { FacetResult } from '../../../models/FacetTypes';
import { FilterAllOption } from '../../../state/FilterState';
import { SearchResultItem, SearchResultList } from '../../../models/SearchResultTypes';
import { CRASSService } from '../../../services/CRASSService';
import { CommonStyles } from '../../../../common/styles/CommonStyles';
import { ContentService } from '../../../services/ContentService';

export interface OwnerFilterStateProps {
  readonly selectedFilterName: string | undefined;
  readonly filterList: FacetResult[];
  readonly filterValues: ListItemWithDisplayText[];
}

export type OwnerFilterProps = QuickFilterOwnProps & QuickFilterStateProps & QuickFilterDispatchProps & RouteComponentProps;

export const OwnerFilterLabelTestID = 'OwnerFilter';

export class OwnerFilter extends QuickFilter<OwnerFilterProps, OwnerFilterStateProps> {
  private prevValue = this.props.filterSelected.toString(); //used to track changes in the value selected for telemetry
  private isFilterMounted: boolean;

  constructor(props: OwnerFilterProps) {
    super(props);
    this.isFilterMounted = false;
    this.state = { selectedFilterName: '', filterList: [], filterValues: [] };
  }

  public componentDidMount(): void {
    this.isFilterMounted = true;
    const ownerIdInUrl = FilterHelpers.getOwnerIdFromUrl();
    if (this.props.filterSelected.value && ownerIdInUrl === this.props.filterSelected.id) {
      //if the owner id matches the one in the url and the name exists, get the value label from the redux state
      this.setState({ selectedFilterName: this.props.filterSelected.value });
    }
    CRASSService.instance.getOwnerList(this.props.searchTerm, this.props.contentType, this.props.allFilterValues).then((result) => {
      const filterList = result ? result : [];
      if (this.isFilterMounted) {
        this.setState({ filterList });
        const filterName = ownerIdInUrl ? FilterHelpers.getSelectedFilterName(filterList, ownerIdInUrl) : undefined;
        if (filterName || !ownerIdInUrl) {
          //owner id is in url and owner name is in the facet list or no owner id in url so it is the owner is anyone option
          const filterValues = FilterHelpers.getFilterValues(filterList, Messages.OwnerFilterAllOption());
          this.setState({ filterValues, selectedFilterName: filterName });
          this.props.updateFilter(ownerIdInUrl || FilterAllOption, filterName || '', FilterTypes.owner);
        } else {
          //if its not in the facet list try to get the name from the redux store and add it to the owner list
          if (this.props.filterSelected.value && this.props.filterSelected.id === ownerIdInUrl) {
            const filterValues = FilterHelpers.getFilterValues(this.state.filterList, Messages.OwnerFilterAllOption(), {
              id: this.props.filterSelected.id,
              value: this.props.filterSelected.value,
            });
            this.setState({ filterValues, selectedFilterName: this.props.filterSelected.value });
          } else {
            //if its not in the redux store because its a Direct Link fetch the user's name based on the id in the url
            ContentService.instance.getMissingUser(ownerIdInUrl).then((r) => {
              if (r?.result && this.isFilterMounted) {
                const selectedFilterName = r.result.displayName;
                const filterValues = FilterHelpers.getFilterValues(filterList, Messages.OwnerFilterAllOption(), {
                  id: ownerIdInUrl,
                  value: selectedFilterName,
                });
                this.setState({ filterValues, selectedFilterName });
                this.props.updateFilter(ownerIdInUrl, selectedFilterName, FilterTypes.owner);
              } else {
                // if there is an incorrect owner id input into the url directly, we we will redirect them to Anyone
                this.onValueChange(FilterAllOption);
              }
            });
          }
        }
      }
    });
  }

  // This is needed because we can't perform a React state update on an unmounted component
  public componentWillUnmount(): void {
    this.isFilterMounted = false;
  }

  public componentDidUpdate(prevProps: OwnerFilterProps, prevState: OwnerFilterStateProps) {
    if (this.isFilterMounted) {
      if (prevProps.filterSelected !== this.props.filterSelected) {
        // Update the dropdown value selected when values are selected within the owner filter
        const filterName = this.props.filterSelected.value;
        const filterNameFromPreviousFilterList = prevState.filterList.find((x) => x.value.id.toString() === this.props.filterSelected.id)
          ?.value.name;
        const selectedFilterName = filterName || filterNameFromPreviousFilterList || prevState.selectedFilterName;
        this.setState({ selectedFilterName });
      } else if (
        prevProps.contentType !== this.props.contentType ||
        prevProps.allFilterValues !== this.props.allFilterValues ||
        (prevState.filterList.length === 0 &&
          FilterHelpers.hasFacets(
            this.props.contentType,
            this.props.singleContentResults,
            this.props.mixedContentResults,
            (searchResultList?: SearchResultList<SearchResultItem>) => searchResultList && searchResultList.facets?.owner.length
          ))
      ) {
        // When other filters change try to get updated facets and keep default selection for the owner filter
        CRASSService.instance.getOwnerList(this.props.searchTerm, this.props.contentType, this.props.allFilterValues).then((result) => {
          const filterList = result ? result : prevState.filterList; // use the old one temporarily
          const filterNameFromList = filterList.find((x) => x.value.id.toString() === this.props.filterSelected.id)?.value.name;
          const selectedFilterName = filterNameFromList || prevState.selectedFilterName || '';
          //if the value is not in the list add it with a 0 count
          const filterValues = FilterHelpers.getFilterValues(
            filterList,
            Messages.OwnerFilterAllOption(),
            filterNameFromList || this.props.filterSelected.id === FilterAllOption
              ? undefined
              : { id: this.props.filterSelected.id, value: selectedFilterName }
          );
          if (this.isFilterMounted) {
            this.setState({ filterValues, selectedFilterName, filterList });
          }
        });
      }
    }
  }

  public render() {
    const buttonStyle =
      this.props.filterSelected.id === FilterAllOption ? CommonStyles.Button.baseButtonStyle : CommonStyles.Button.blueButtonStyle;
    const buttonClass = this.props.filterSelected.id === FilterAllOption ? DropdownDefaultButton : DropdownBlueButton;
    return (
      <SingleSelectDropdown
        label=""
        list={
          <LocallyFilteringList
            style={{ maxHeight: window.innerHeight / 2, minWidth: '300px' }}
            items={this.state.filterValues}
            itemFilterFn={(item, filterValue) => (item as ListItemWithDisplayText).displayText.toLowerCase().indexOf(filterValue) !== -1}
            searchPlaceholder={Messages.OwnerInputPlaceholder()}
            className={FilteringListClass}
          />
        }
        style={{ marginLeft: '10px', minWidth: '100px', ...buttonStyle }}
        type={DropdownButtonType.Outline}
        value={this.props.filterSelected.id}
        onChange={(value) => this.onValueChange(value)}
        className={buttonClass}
      >
        {this.props.filterSelected.id === FilterAllOption ? (
          <span data-tb-test-id={OwnerFilterLabelTestID}>
            <span>{Messages.OwnerSubTitle() + ' '}</span>
            <b>{Messages.OwnerFilterAllOption()}</b>
          </span>
        ) : (
          <span data-tb-test-id={OwnerFilterLabelTestID}>
            <b>{this.state.selectedFilterName}</b>
          </span>
        )}
      </SingleSelectDropdown>
    );
  }

  private onValueChange(id: string): void {
    this.sendFiltersUpdatedTelemetryEvent(id, this.prevValue, FilterTypes.owner, this.props.contentType);
    const filterName = FilterHelpers.getSelectedFilterName(this.state.filterList, id);
    this.onDropDownValueChanged(id, FilterTypes.owner, filterName || '');
    this.prevValue = id.toString();
  }
}

const mapStateToProps = (state: AppState) => ({
  filterSelected: state.filters.query[FilterTypes.owner],
  allFilterValues: state.filters.query,
  contentType: state.search.selectedContentTypeFilterValue,
  mixedContentResults: state.search.mixedContentType.searchResult,
  singleContentResults: state.singleContentType.searchResults,
  isLoadingFilterContent: state.filters.isLoadingFilterContent,
  searchTerm: state.search.searchTerm,
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    {
      updateFilter,
    },
    dispatch
  );

const OwnerFilterWithRouter = withRouter(OwnerFilter);

export const ConnectedOwnerFilter: React.ComponentClass<QuickFilterOwnProps> = connect(
  mapStateToProps,
  mapDispatchToProps
)(OwnerFilterWithRouter);
